import React, { useMemo, useState } from 'react';
import { filterRowsBySpace } from '../utils/venueSpacesUi';
import TimeRangePicker from '../../Common/TimeRangePicker';
import CustomDatePicker from '../../Common/CustomDatePicker';
import { formatTime } from '../utils/dateUtils';
import {
  FigmaCalendarOutlineIcon,
  FigmaTrashIcon,
  FigmaAddSquareIcon,
  FigmaAddCircleIcon,
  FigmaMinusCircleIcon
} from '../../icons';
import { isPerPersonBookingEnabled } from '../../../utils/bookingPricingMetadata';
import SpaceScopePicker from './SettingsSpaceScopeBar';

const PricingControlsForm = ({
  defaultPrice,
  onDefaultPriceChange,
  defaultPerPersonPrice,
  onDefaultPerPersonPriceChange,
  dynamicPricing,
  onPricingDelete,
  dynamicPricingPerPerson = [],
  onPricingPerPersonDelete,
  draftDynamicPricing,
  onDraftPricingChange,
  addAnotherDynamicPricing,
  removeDraftPricing,
  draftDynamicPricingPerPerson = [],
  onDraftPricingPerPersonChange,
  addAnotherDynamicPricingPerPerson,
  removeDraftPricingPerPerson,
  discountCodes = [],
  onCreateDiscountCode,
  onDeleteDiscountCode,
  onNotify,
  flatPricing = [],
  draftFlatPricing = [],
  onFlatPricingDelete,
  addDraftFlatPricing,
  removeDraftFlatPricing,
  onFlatPricingRowChange,
  bookingRules = {},
  onBookingRulesChange,
  venueSpaces = [],
  settingsSpaceId = null,
  onSettingsSpaceIdChange,
  multiSpace = false,
  scopeLoading = false
}) => {
  const [draftFlatInput, setDraftFlatInput] = useState({ min_hours: '', flat_price: '' });
  const [draftDiscount, setDraftDiscount] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    expires_at: ''
  });
  const [discountSaving, setDiscountSaving] = useState(false);
  const [discountActionError, setDiscountActionError] = useState('');
  const [deletingDiscountId, setDeletingDiscountId] = useState(null);

  const minHoursExclusive = bookingRules?.min_hours_exclusive ?? bookingRules?.min_total_hours ?? 2;
  const minHoursPerPerson = bookingRules?.min_hours_per_person ?? 2;
  const postEventBufferMinutes = Number(bookingRules?.post_event_buffer_minutes) || 0;
  const postEventBufferLabel = postEventBufferMinutes > 0 ? `${postEventBufferMinutes} mins` : 'Off';
  const perPersonEnabled = isPerPersonBookingEnabled(bookingRules);

  const spaceFilterOpts = useMemo(
    () => ({ includeVenueWide: !(multiSpace && settingsSpaceId) }),
    [multiSpace, settingsSpaceId]
  );

  const scopedDynamicPricing = useMemo(
    () =>
      multiSpace && settingsSpaceId
        ? filterRowsBySpace(dynamicPricing, settingsSpaceId, spaceFilterOpts)
        : dynamicPricing,
    [dynamicPricing, multiSpace, settingsSpaceId, spaceFilterOpts]
  );

  const scopedDynamicPricingPerPerson = useMemo(
    () =>
      multiSpace && settingsSpaceId
        ? filterRowsBySpace(dynamicPricingPerPerson, settingsSpaceId, spaceFilterOpts)
        : dynamicPricingPerPerson,
    [dynamicPricingPerPerson, multiSpace, settingsSpaceId, spaceFilterOpts]
  );

  const scopedFlatPricing = useMemo(
    () =>
      multiSpace && settingsSpaceId
        ? filterRowsBySpace(flatPricing, settingsSpaceId, spaceFilterOpts)
        : flatPricing,
    [flatPricing, multiSpace, settingsSpaceId, spaceFilterOpts]
  );

  const handleAddFlatPricing = () => {
    const minHours = parseFloat(draftFlatInput.min_hours);
    const flatPrice = parseFloat(draftFlatInput.flat_price);
    if (!minHours || minHours <= 0 || !flatPrice || flatPrice <= 0) {
      return;
    }
    addDraftFlatPricing({ min_hours: minHours, flat_price: flatPrice });
    setDraftFlatInput({ min_hours: '', flat_price: '' });
  };

  const formatPricingDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatPricingDateRange = (startDateStr, endDateStr) => {
    if (!startDateStr) return '';
    if (!endDateStr || startDateStr === endDateStr) {
      return formatPricingDate(startDateStr);
    }
    return `${formatPricingDate(startDateStr)} to ${formatPricingDate(endDateStr)}`;
  };

  const formatDiscountValue = (discountType, discountValue) => {
    const value = Number(discountValue) || 0;
    const type = String(discountType || 'percentage').toLowerCase();
    if (type === 'fixed' || type === 'gbp' || type === 'amount') {
      return `£${value.toFixed(2)}`;
    }
    return `${value}%`;
  };

  const formatDiscountExpiry = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDiscountStatus = (discountCode) => {
    if (discountCode?.used_at) return 'Used';
    return discountCode?.status ? String(discountCode.status).replace(/_/g, ' ') : 'active';
  };

  const handleCreateDiscountCode = async () => {
    if (!onCreateDiscountCode) return;

    const discountValue = Number.parseFloat(draftDiscount.discount_value);
    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      setDiscountActionError('Enter a discount value greater than 0.');
      return;
    }
    if (draftDiscount.discount_type === 'percentage' && discountValue > 100) {
      setDiscountActionError('Percentage discounts cannot exceed 100%.');
      return;
    }

    const code = draftDiscount.code.trim().toUpperCase();
    if (code && !/^[A-Z0-9_-]{3,32}$/.test(code)) {
      setDiscountActionError('Codes must be 3–32 characters and use letters, numbers, hyphens, or underscores.');
      return;
    }

    setDiscountSaving(true);
    setDiscountActionError('');
    try {
      await onCreateDiscountCode({
        ...(code ? { code } : {}),
        discount_type: draftDiscount.discount_type,
        discount_value: discountValue,
        ...(draftDiscount.expires_at
          ? { expires_at: new Date(`${draftDiscount.expires_at}T23:59:59`).toISOString() }
          : {})
      });
      setDraftDiscount({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        expires_at: ''
      });
      onNotify?.({
        type: 'success',
        title: 'Discount code created',
        message: code ? `Code ${code} is ready to use.` : 'Your new discount code is ready to use.'
      });
    } catch (error) {
      const message = error?.message || 'Failed to create discount code.';
      setDiscountActionError(message);
      onNotify?.({ type: 'error', title: 'Discount code', message });
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleDeleteDiscountCode = async (discountCode) => {
    if (!onDeleteDiscountCode || !discountCode?.id) return;
    if (discountCode.used_at) return;

    setDeletingDiscountId(discountCode.id);
    setDiscountActionError('');
    try {
      await onDeleteDiscountCode(discountCode.id);
      onNotify?.({
        type: 'success',
        title: 'Discount code removed',
        message: `${discountCode.code || 'Code'} has been deleted.`
      });
    } catch (error) {
      const message = error?.message || 'Failed to delete discount code.';
      setDiscountActionError(message);
      onNotify?.({ type: 'error', title: 'Discount code', message });
    } finally {
      setDeletingDiscountId(null);
    }
  };

  return (
    <div className="settings-panel pricing-controls-panel" style={{ width: '100%' }}>
      {multiSpace ? (
        <SpaceScopePicker
          spaces={venueSpaces}
          value={settingsSpaceId}
          onChange={onSettingsSpaceIdChange}
          venueWideLabel="Venue default pricing"
        />
      ) : null}
      {scopeLoading ? (
        <p className="pricing-helper-text">Loading pricing for selected space…</p>
      ) : null}
      <div className="pricing-section">
        <label className="pricing-section-title">General pricing</label>
        <div className="pricing-fields">
          <div className="pricing-field pricing-field--full-width">
            <label className="pricing-field-label">Per person booking</label>
            <p className="pricing-field-hint">
              When disabled, customers only see full venue hire and its availability on the booking page.
            </p>
            <div className="pricing-booking-mode-options" role="radiogroup" aria-label="Per person booking">
              <label className="pricing-booking-mode-option">
                <input
                  type="radio"
                  name="perPersonEnabled"
                  checked={perPersonEnabled}
                  onChange={() => onBookingRulesChange?.({
                    ...bookingRules,
                    per_person_enabled: true,
                  })}
                />
                <span>Enabled</span>
              </label>
              <label className="pricing-booking-mode-option">
                <input
                  type="radio"
                  name="perPersonEnabled"
                  checked={!perPersonEnabled}
                  onChange={() => onBookingRulesChange?.({
                    ...bookingRules,
                    per_person_enabled: false,
                  })}
                />
                <span>Disabled (full venue hire only)</span>
              </label>
            </div>
          </div>
          <div className="pricing-field">
            <label className="pricing-field-label">
              Minimum hours (full venue hire) <span className="required-asterisk">*</span>
            </label>
            <div className="pricing-input duration-input">
              <input
                type="text"
                className="form-input pricing-input-field"
                value={`${minHoursExclusive} hours`}
                readOnly
              />
              <div className="pricing-duration-controls">
                <button
                  type="button"
                  className="duration-control-btn"
                  onClick={() => onBookingRulesChange?.({ ...bookingRules, min_hours_exclusive: Math.max(1, minHoursExclusive - 1) })}
                  aria-label="Decrease minimum hours"
                >
                  <FigmaMinusCircleIcon />
                </button>
                <button
                  type="button"
                  className="duration-control-btn"
                  onClick={() => onBookingRulesChange?.({ ...bookingRules, min_hours_exclusive: minHoursExclusive + 1 })}
                  aria-label="Increase minimum hours"
                >
                  <FigmaAddCircleIcon />
                </button>
              </div>
            </div>
          </div>
          <div className="pricing-field">
            <label className="pricing-field-label">
              Minimum hours (per person) <span className="required-asterisk">*</span>
            </label>
            <div className="pricing-input duration-input">
              <input
                type="text"
                className="form-input pricing-input-field"
                value={`${minHoursPerPerson} hours`}
                readOnly
              />
              <div className="pricing-duration-controls">
                <button
                  type="button"
                  className="duration-control-btn"
                  onClick={() => onBookingRulesChange?.({ ...bookingRules, min_hours_per_person: Math.max(1, minHoursPerPerson - 1) })}
                  aria-label="Decrease minimum hours"
                >
                  <FigmaMinusCircleIcon />
                </button>
                <button
                  type="button"
                  className="duration-control-btn"
                  onClick={() => onBookingRulesChange?.({ ...bookingRules, min_hours_per_person: minHoursPerPerson + 1 })}
                  aria-label="Increase minimum hours"
                >
                  <FigmaAddCircleIcon />
                </button>
              </div>
            </div>
          </div>
          <div className="pricing-field">
            <label className="pricing-field-label">Buffer after confirmed events</label>
            <div className="pricing-input duration-input">
              <input
                type="text"
                className="form-input pricing-input-field"
                value={postEventBufferLabel}
                readOnly
              />
              <div className="pricing-duration-controls">
                <button
                  type="button"
                  className="duration-control-btn"
                  onClick={() => {
                    const next = postEventBufferMinutes <= 1 ? null : postEventBufferMinutes - 1;
                    onBookingRulesChange?.({ ...bookingRules, post_event_buffer_minutes: next });
                  }}
                  aria-label="Decrease buffer minutes"
                >
                  <FigmaMinusCircleIcon />
                </button>
                <button
                  type="button"
                  className="duration-control-btn"
                  onClick={() => {
                    const next = postEventBufferMinutes <= 0 ? 1 : Math.min(60, postEventBufferMinutes + 1);
                    onBookingRulesChange?.({ ...bookingRules, post_event_buffer_minutes: next });
                  }}
                  aria-label="Increase buffer minutes"
                >
                  <FigmaAddCircleIcon />
                </button>
              </div>
            </div>
          </div>
          <div className="pricing-field">
            <label className="pricing-field-label">
              Default price <span className="required-asterisk">*</span>
            </label>
            <div className="pricing-input">
              <span className="pricing-input-prefix">£</span>
              <input
                type="number"
                className="form-input pricing-input-field"
                value={defaultPrice}
                onChange={(e) => onDefaultPriceChange(Number(e.target.value))}
              />
              <span className="pricing-input-suffix">/ hour</span>
            </div>
          </div>
          <div className="pricing-field">
            <label className="pricing-field-label">
              Default per-person price <span className="required-asterisk">*</span>
            </label>
            <div className="pricing-input">
              <span className="pricing-input-prefix">£</span>
              <input
                type="number"
                className="form-input pricing-input-field"
                value={defaultPerPersonPrice ?? ''}
                onChange={(e) => onDefaultPerPersonPriceChange?.(Number(e.target.value) || 0)}
              />
              <span className="pricing-input-suffix">/ person/hr</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pricing-section">
        <label className="pricing-section-title">Dynamic pricing</label>
        <div className="dynamic-pricing-table figma-table pricing-table">
          <div className="table-header figma-table-header">
            <div className="table-cell">DATE</div>
            <div className="table-cell">TIME</div>
            <div className="table-cell">PRICE</div>
            <div className="table-cell action-cell"></div>
          </div>
          {scopedDynamicPricing.map((pricing) => (
            <div key={pricing.id} className="table-row figma-table-row">
              <div className="table-cell">
                <FigmaCalendarOutlineIcon className="icon-calendar" />
                <span>{formatPricingDateRange(pricing.start_date, pricing.end_date)}</span>
              </div>
              <div className="table-cell">
                {pricing.start_time && pricing.end_time ? (
                  <span>
                    {formatTime(pricing.start_time)} - {formatTime(pricing.end_time)}
                  </span>
                ) : (
                  <span className="muted">All day</span>
                )}
              </div>
              <div className="table-cell price-cell">
                <span>£{pricing.price_per_hour}</span>
                <span className="pricing-input-suffix">/ hour</span>
              </div>
              <div className="table-cell action-cell">
                <button
                  className="icon-button"
                  onClick={() => onPricingDelete(pricing.id)}
                  type="button"
                  aria-label="Delete pricing"
                >
                  <FigmaTrashIcon className="icon-trash" />
                </button>
              </div>
            </div>
          ))}
          {draftDynamicPricing.map((draft, index) => (
            <div key={index} className="table-row figma-table-row">
              <div className="table-cell date-range-cell">
                <div className="date-range-pickers">
                  <CustomDatePicker
                    value={draft.date}
                    onChange={(date) =>
                      onDraftPricingChange(index, {
                        date,
                        endDate: draft.endDate,
                        startTime: draft.startTime,
                        endTime: draft.endTime,
                        price: draft.price
                      })
                    }
                    placeholder="Start date"
                    className="form-input pricing-date-picker"
                  />
                  <span className="date-range-divider">to</span>
                  <CustomDatePicker
                    value={draft.endDate}
                    onChange={(date) =>
                      onDraftPricingChange(index, {
                        date: draft.date,
                        endDate: date,
                        startTime: draft.startTime,
                        endTime: draft.endTime,
                        price: draft.price
                      })
                    }
                    placeholder="End date (optional)"
                    className="form-input pricing-date-picker"
                    minDate={draft.date || undefined}
                  />
                </div>
              </div>
              <div className="table-cell">
                <TimeRangePicker
                  startTime={draft.startTime}
                  endTime={draft.endTime}
                  onStartChange={(time) =>
                    onDraftPricingChange(index, {
                      date: draft.date,
                      endDate: draft.endDate,
                      startTime: time,
                      endTime: draft.endTime,
                      price: draft.price
                    })
                  }
                  onEndChange={(time) =>
                    onDraftPricingChange(index, {
                      date: draft.date,
                      endDate: draft.endDate,
                      startTime: draft.startTime,
                      endTime: time,
                      price: draft.price
                    })
                  }
                  startPlaceholder="Start"
                  endPlaceholder="End"
                  dropdownBelow
                />
              </div>
              <div className="table-cell price-cell">
                <span className="pricing-input-prefix">£</span>
                <input
                  type="number"
                  placeholder="Enter price"
                  className="form-input pricing-input-field"
                  value={draft.price}
                  onChange={(e) =>
                    onDraftPricingChange(index, {
                      date: draft.date,
                      endDate: draft.endDate,
                      startTime: draft.startTime,
                      endTime: draft.endTime,
                      price: e.target.value
                    })
                  }
                  min="0"
                  step="0.01"
                />
                <span className="pricing-input-suffix">/ hour</span>
              </div>
              <div className="table-cell action-cell">
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Delete draft pricing"
                  onClick={() => removeDraftPricing(index)}
                >
                  <FigmaTrashIcon className="icon-trash" />
                </button>
              </div>
            </div>
          ))}
          <div className="table-row add-holiday-row">
            <button
              className="add-holiday-btn"
              type="button"
              onClick={addAnotherDynamicPricing}
            >
              <FigmaAddSquareIcon className="icon-add-square" />
              Add another
            </button>
          </div>
        </div>
      </div>

      <div className="pricing-section">
        <label className="pricing-section-title">Dynamic per-person pricing</label>
        <p className="pricing-helper-text" style={{ fontSize: '14px', color: '#585858', marginBottom: '12px' }}>
          Override the default per-person rate for specific dates (e.g. peak season).
        </p>
        <div className="dynamic-pricing-table figma-table pricing-table">
          <div className="table-header figma-table-header">
            <div className="table-cell">DATE</div>
            <div className="table-cell">TIME</div>
            <div className="table-cell">PRICE</div>
            <div className="table-cell action-cell"></div>
          </div>
          {(scopedDynamicPricingPerPerson || []).map((pricing) => (
            <div key={pricing.id} className="table-row figma-table-row">
              <div className="table-cell">
                <FigmaCalendarOutlineIcon className="icon-calendar" />
                <span>{formatPricingDateRange(pricing.start_date, pricing.end_date)}</span>
              </div>
              <div className="table-cell">
                {pricing.start_time && pricing.end_time ? (
                  <span>
                    {formatTime(pricing.start_time)} - {formatTime(pricing.end_time)}
                  </span>
                ) : (
                  <span className="muted">All day</span>
                )}
              </div>
              <div className="table-cell price-cell">
                <span>£{pricing.price_per_person}</span>
                <span className="pricing-input-suffix">/ person</span>
              </div>
              <div className="table-cell action-cell">
                <button
                  className="icon-button"
                  onClick={() => onPricingPerPersonDelete?.(pricing.id)}
                  type="button"
                  aria-label="Delete per-person pricing"
                >
                  <FigmaTrashIcon className="icon-trash" />
                </button>
              </div>
            </div>
          ))}
          {(draftDynamicPricingPerPerson || []).map((draft, index) => (
            <div key={`pp-${index}`} className="table-row figma-table-row">
              <div className="table-cell date-range-cell">
                <div className="date-range-pickers">
                  <CustomDatePicker
                    value={draft.date}
                    onChange={(date) =>
                      onDraftPricingPerPersonChange?.(index, {
                        date,
                        endDate: draft.endDate,
                        startTime: draft.startTime,
                        endTime: draft.endTime,
                        price: draft.price
                      })
                    }
                    placeholder="Start date"
                    className="form-input pricing-date-picker"
                  />
                  <span className="date-range-divider">to</span>
                  <CustomDatePicker
                    value={draft.endDate}
                    onChange={(date) =>
                      onDraftPricingPerPersonChange?.(index, {
                        date: draft.date,
                        endDate: date,
                        startTime: draft.startTime,
                        endTime: draft.endTime,
                        price: draft.price
                      })
                    }
                    placeholder="End date (optional)"
                    className="form-input pricing-date-picker"
                    minDate={draft.date || undefined}
                  />
                </div>
              </div>
              <div className="table-cell">
                <TimeRangePicker
                  startTime={draft.startTime}
                  endTime={draft.endTime}
                  onStartChange={(time) =>
                    onDraftPricingPerPersonChange?.(index, {
                      date: draft.date,
                      endDate: draft.endDate,
                      startTime: time,
                      endTime: draft.endTime,
                      price: draft.price
                    })
                  }
                  onEndChange={(time) =>
                    onDraftPricingPerPersonChange?.(index, {
                      date: draft.date,
                      endDate: draft.endDate,
                      startTime: draft.startTime,
                      endTime: time,
                      price: draft.price
                    })
                  }
                  startPlaceholder="Start"
                  endPlaceholder="End"
                  dropdownBelow
                />
              </div>
              <div className="table-cell price-cell">
                <span className="pricing-input-prefix">£</span>
                <input
                  type="number"
                  placeholder="Enter price"
                  className="form-input pricing-input-field"
                  value={draft.price}
                  onChange={(e) =>
                    onDraftPricingPerPersonChange?.(index, {
                      date: draft.date,
                      endDate: draft.endDate,
                      startTime: draft.startTime,
                      endTime: draft.endTime,
                      price: e.target.value
                    })
                  }
                  min="0"
                  step="0.01"
                />
                <span className="pricing-input-suffix">/ person</span>
              </div>
              <div className="table-cell action-cell">
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Delete draft per-person pricing"
                  onClick={() => removeDraftPricingPerPerson?.(index)}
                >
                  <FigmaTrashIcon className="icon-trash" />
                </button>
              </div>
            </div>
          ))}
          <div className="table-row add-holiday-row">
            <button
              className="add-holiday-btn"
              type="button"
              onClick={addAnotherDynamicPricingPerPerson}
            >
              <FigmaAddSquareIcon className="icon-add-square" />
              Add another
            </button>
          </div>
        </div>
      </div>

      <div className="pricing-section">
        <label className="pricing-section-title">Flat pricing</label>
        <p className="pricing-helper-text" style={{ fontSize: '14px', color: '#585858', marginBottom: '12px' }}>
          When X hours is booked, a flat price applies instead of hourly rate (e.g. book 4+ hours = £200 flat).
        </p>
        <div className="flat-pricing-table figma-table">
          <div className="table-header figma-table-header">
            <div className="table-cell">WHEN HOURS BOOKED</div>
            <div className="table-cell">FLAT PRICE</div>
            <div className="table-cell action-cell"></div>
          </div>
          {scopedFlatPricing.map((row) => (
            <div key={row.id} className="table-row figma-table-row">
              <div className="table-cell">
                <input
                  type="number"
                  placeholder="Hours"
                  className="form-input pricing-input-field"
                  value={row.min_hours ?? ''}
                  onChange={(e) => onFlatPricingRowChange?.(row.id, { min_hours: parseFloat(e.target.value) || 0 })}
                  min="1"
                  step="0.5"
                />
                <span className="pricing-input-suffix">hours</span>
              </div>
              <div className="table-cell price-cell">
                <span className="pricing-input-prefix">£</span>
                <input
                  type="number"
                  placeholder="Flat price"
                  className="form-input pricing-input-field"
                  value={row.flat_price ?? ''}
                  onChange={(e) => onFlatPricingRowChange?.(row.id, { flat_price: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="table-cell action-cell">
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Delete flat pricing"
                  onClick={() => onFlatPricingDelete?.(row.id)}
                >
                  <FigmaTrashIcon className="icon-trash" />
                </button>
              </div>
            </div>
          ))}
          {draftFlatPricing.map((draft, index) => (
            <div key={draft._draftId} className="table-row figma-table-row">
              <div className="table-cell">
                <input
                  type="number"
                  placeholder="Hours"
                  className="form-input pricing-input-field"
                  value={draft.min_hours ?? ''}
                  readOnly
                  min="1"
                  step="0.5"
                />
                <span className="pricing-input-suffix">hours</span>
              </div>
              <div className="table-cell price-cell">
                <span className="pricing-input-prefix">£</span>
                <input
                  type="number"
                  placeholder="Flat price"
                  className="form-input pricing-input-field"
                  value={draft.flat_price ?? ''}
                  readOnly
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="table-cell action-cell">
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Remove draft flat pricing"
                  onClick={() => removeDraftFlatPricing?.(index)}
                >
                  <FigmaTrashIcon className="icon-trash" />
                </button>
              </div>
            </div>
          ))}
          <div className="table-row figma-table-row">
            <div className="table-cell">
              <input
                type="number"
                placeholder="Hours"
                className="form-input pricing-input-field"
                value={draftFlatInput.min_hours}
                onChange={(e) => setDraftFlatInput(prev => ({ ...prev, min_hours: e.target.value }))}
                min="1"
                step="0.5"
              />
              <span className="pricing-input-suffix">hours</span>
            </div>
            <div className="table-cell price-cell">
              <span className="pricing-input-prefix">£</span>
              <input
                type="number"
                placeholder="Flat price"
                className="form-input pricing-input-field"
                value={draftFlatInput.flat_price}
                onChange={(e) => setDraftFlatInput(prev => ({ ...prev, flat_price: e.target.value }))}
                min="0"
                step="0.01"
              />
            </div>
            <div className="table-cell action-cell">
              <button className="add-holiday-btn" type="button" onClick={handleAddFlatPricing}>
                <FigmaAddSquareIcon className="icon-add-square" />
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="pricing-section">
        <label className="pricing-section-title">Discount codes</label>
        <p className="pricing-section-description">
          Create codes customers can apply when booking your venue through Occasia.
        </p>

        <div className="discount-codes-table figma-table pricing-table">
          <div className="table-header figma-table-header">
            <div className="table-cell">Code</div>
            <div className="table-cell">Discount</div>
            <div className="table-cell">Expires</div>
            <div className="table-cell">Status</div>
            <div className="table-cell action-cell" />
          </div>

          {discountCodes.length > 0 ? (
            discountCodes.map((dc) => (
              <div key={dc.id || dc.code} className="table-row figma-table-row">
                <div className="table-cell table-cell--code">{dc.code || dc.discount_code}</div>
                <div className="table-cell">
                  {formatDiscountValue(dc.discount_type, dc.discount_value)}
                </div>
                <div className="table-cell">{formatDiscountExpiry(dc.expires_at)}</div>
                <div className="table-cell table-cell--status">{formatDiscountStatus(dc)}</div>
                <div className="table-cell action-cell">
                  <button
                    className="icon-button"
                    type="button"
                    aria-label={`Delete discount code ${dc.code || ''}`}
                    disabled={Boolean(dc.used_at) || deletingDiscountId === dc.id}
                    onClick={() => handleDeleteDiscountCode(dc)}
                  >
                    <FigmaTrashIcon className="icon-trash" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="discount-codes-empty-row">
              <p>No discount codes yet.</p>
            </div>
          )}
        </div>

        <div className="discount-codes-create-panel">
          <div className="discount-codes-create-fields">
            <div className="pricing-field">
              <label className="pricing-field-label">Code</label>
              <input
                type="text"
                placeholder="Auto-generate"
                className="form-input pricing-input-field discount-codes-create-input"
                value={draftDiscount.code}
                onChange={(e) =>
                  setDraftDiscount((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                }
                maxLength={32}
              />
            </div>
            <div className="pricing-field">
              <label className="pricing-field-label">Discount</label>
              <div className="discount-codes-draft-value">
                <select
                  className="form-input pricing-input-field discount-codes-type-select"
                  value={draftDiscount.discount_type}
                  onChange={(e) =>
                    setDraftDiscount((prev) => ({ ...prev, discount_type: e.target.value }))
                  }
                >
                  <option value="percentage">%</option>
                  <option value="fixed">£</option>
                </select>
                <input
                  type="number"
                  placeholder={draftDiscount.discount_type === 'percentage' ? '10' : '25'}
                  className="form-input pricing-input-field discount-codes-create-input"
                  value={draftDiscount.discount_value}
                  onChange={(e) =>
                    setDraftDiscount((prev) => ({ ...prev, discount_value: e.target.value }))
                  }
                  min="0"
                  step={draftDiscount.discount_type === 'percentage' ? '1' : '0.01'}
                />
              </div>
            </div>
            <div className="pricing-field">
              <label className="pricing-field-label">Expires</label>
              <input
                type="date"
                className="form-input pricing-input-field discount-codes-create-input"
                value={draftDiscount.expires_at}
                onChange={(e) =>
                  setDraftDiscount((prev) => ({ ...prev, expires_at: e.target.value }))
                }
              />
            </div>
          </div>
          <button
            className="discount-codes-add-btn"
            type="button"
            disabled={discountSaving || !onCreateDiscountCode}
            onClick={handleCreateDiscountCode}
          >
            <FigmaAddSquareIcon className="icon-add-square" />
            {discountSaving ? 'Adding…' : 'Add discount code'}
          </button>
        </div>

        {discountActionError ? (
          <p className="pricing-inline-error">{discountActionError}</p>
        ) : null}
      </div>
    </div>
  );
};

export default PricingControlsForm;

